// (웹/앱) 수업 리뷰 > AI 진단 개요 > 내가 집중하고 싶은 학습 영역
import { HighlightOverviewResponse } from '../../../../model/caf.interface'
import { useCafHighlightListV5 } from '../../../../modules/apis/webview/caf'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import urls from '../../../../modules/urls'
import SimpleHighlight from './SimpleHighlight'

import { BUTTON_SIZE, DSButton } from '@repo/design-system/index'
import { QIcon_chevron_bold_right } from '@repo/icons/index'
import { useEffect } from 'react'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../../modules/hooks/common/useIsWebview'
import useRNMsg from '../../../../modules/hooks/webview/common/useRNMsg'
import useWebviewNavigation from '../../../../modules/hooks/webview/common/useWebviewNavigation'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { COLORS } from '../../../../modules/vars'
import { LESSON_ACTIVITIES_MAP, useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import LoadingSpinner from '../../../common/LoadingSpinner'
import ThickDivider from '../common/ThickDivider'

const PinnedHighlights = ({ lessonId, level }: { lessonId: number; level: number }) => {
  const { navigate } = useWebviewNavigation()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const router = useAppRouter()
  const isWebview = useIsWebview()
  const { useLessonsFlow } = useLessonsStack()
  const { push } = useLessonsFlow()
  const goHighlights = () => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIOverviewHighlightListActivity, { pinned: false, lessonId })
    } else {
      router.push(urls.student.newPortal.lessons.reviewDetail.asCaf(lessonId, 5))
    }
  }
  const openEditPopup = () => {
    if (isWebview) {
      push(LESSON_ACTIVITIES_MAP.AIOverviewHighlightListActivity, { pinned: true, lessonId })
    } else {
      router.push(urls.student.newPortal.lessons.reviewDetail.asCafEditPopup(lessonId, true))
    }
  }
  const { data, refetch, isLoading } = useCafHighlightListV5<HighlightOverviewResponse>({
    lessonId,
    pinned: true,
    show_details: true,
  })
  const { receivePostMsg, removeListener } = useRNMsg()

  useEffect(() => {
    const callback = (msg) => {
      if (msg === 'focus') {
        refetch()
      }
    }
    receivePostMsg((message) => callback(message))
    return () => {
      removeListener(callback)
    }
  }, [receivePostMsg, refetch, removeListener])

  if (isMobile) {
    return (
      <>
        <ThickDivider />
        <section className="w-full py-[16px]">
          <div className="mb-[12px] flex items-center justify-between">
            <div className="text-sTitle font-bold text-black">{t(n_caf.overview.tophighlights_title)}</div>
            {data?.highlights?.length !== 0 && (
              <div
                className="Body2-14Bd flex cursor-pointer flex-row items-center justify-start text-gray-500"
                onClick={openEditPopup}
              >
                {t(n_caf.overview.tophighlights_edit)}
              </div>
            )}
          </div>
          {data?.highlights?.length === 0 && (
            <div className="text-h5 relative flex flex-col items-center justify-center rounded-[6px] bg-gray-100 py-[16px] text-center font-medium text-purple-500">
              <div>{t(n_caf.overview.tophighlights_info)}</div>
              <div className="mt-[12px]">
                <DSButton
                  size={BUTTON_SIZE.SM}
                  customBgColor="white"
                  className="!w-auto border-[1.5px] px-[16px] py-[8px]"
                  customBorderColor={COLORS.PURPLE500}
                  onClick={goHighlights}
                >
                  <DSButton.Label customTxtColor={COLORS.PURPLE500} className="text-center">
                    {t(n_caf.overview.checkout.btn)}
                  </DSButton.Label>
                </DSButton>
              </div>
            </div>
          )}
          {data?.highlights?.map((highlightItem) => (
            <SimpleHighlight key={highlightItem.id} highlightItem={highlightItem} type={'pinned'} />
          ))}
        </section>
      </>
    )
  } else {
    return (
      <>
        <ThickDivider />
        <section className="w-full py-[32px]">
          <div className="mb-[24px] flex w-full items-center justify-between">
            <div className="text-[20px] font-bold text-black">{t(n_caf.overview.tophighlights_title)}</div>
            {data?.highlights?.length !== 0 && (
              <div
                className="flex cursor-pointer flex-row items-center justify-start text-[14px] font-bold text-black"
                onClick={openEditPopup}
              >
                {t(n_caf.overview.tophighlights_edit)}
                <QIcon_chevron_bold_right
                  className="ml-2 h-[16px] w-[16px]"
                  height={16}
                  width={16}
                  fill={COLORS.GRAY600}
                  color={COLORS.GRAY600}
                />
              </div>
            )}
          </div>
          {data?.highlights?.length === 0 && (
            <div className="text-h5 flex flex-col items-center justify-between rounded-[6px] bg-gray-100 p-[16px] text-center font-medium text-purple-500">
              <div className="text-[16px] text-purple-500">{t(n_caf.overview.tophighlights_info1)}</div>
              <div className="mt-[12px]">
                <DSButton
                  customBorderColor={COLORS.PURPLE600}
                  customBgColor="white"
                  className="border-[1.5px] px-[16px] py-[8px]"
                  onClick={goHighlights}
                >
                  <DSButton.Label customTxtColor={COLORS.PURPLE600} className="text-center">
                    {t(n_caf.overview.checkout.btn)}
                  </DSButton.Label>
                </DSButton>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex h-[198px] w-[100%] flex-row items-center justify-center">
              <LoadingSpinner show primary lg />
            </div>
          ) : (
            <div className="flex w-full flex-wrap gap-[16px] sm:w-[352px] lg:w-[596px] xl:w-[688px]">
              {data?.highlights?.map((highlightItem) => (
                <SimpleHighlight key={highlightItem.id} highlightItem={highlightItem} type="pinned" />
              ))}
            </div>
          )}
        </section>
      </>
    )
  }
}

export default PinnedHighlights
